import {
  EditorProvider,
  EditorProviderProps,
  Content,
  useCurrentEditor,
} from '@tiptap/react'
import { Editor, JSONContent } from '@tiptap/core'
import * as React from 'react'
import { clsx } from 'clsx'
import debounce from 'lodash.debounce'

import { getFeedbackExtensions } from './extensions'
import mentionClasses from './extensions/mentions/mention.module.scss'
import vcursorstyles from '~/lib/tiptap/extensions/rich-text/extensions/fake-cursor/fake-cursor.module.scss'
import '~/lib/tiptap/components/primitive-tip-tap.css'

import {
  getImageDimensions,
  extractVideoThumbnailAndDimensions,
  startImageUpload,
  uploadAndReplaceImages,
} from '~/lib/tiptap/plugins/upload-image'
import { startVideoUpload } from '~/lib/tiptap/plugins/upload-video'
import {
  SUPPORTED_LLM_FILE_TYPES,
  productionR2Origin,
} from '@basehub/shared-constants'

// ────────────────────────────────────────────────────────────────────────────────
// Stores / Hooks
// ────────────────────────────────────────────────────────────────────────────────
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useTabsStore } from '../../../explore/components/tabs/store'
import { useRepoMembers } from '~/hooks-data/use-repo-members'
import { useUserStore } from '~/app/_store/user-store'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useCommentDraftStorage } from './use-comment-draft-storage'

// ────────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────────
import { PublicUser } from '~/app/api/users/public-user-schema'
import { Block } from '@basehub/data/replicache/index'
import { TreeNode } from '@basehub/utils/tree/index'
import { MentionsListProps } from './extensions/mentions/mentions-list'
import { BlockMentionProps } from './extensions/mentions/blocks-mention'
import { invariant } from '@epic-web/invariant'
import { getId } from '@basehub/utils/id/index'
import { useSearchRepo } from '~/hooks/use-search-repo'
import { startFileUpload } from '~/lib/tiptap/plugins/upload-file'
import { toast } from 'sonner'

// ────────────────────────────────────────────────────────────────────────────────
// Component Props
// ────────────────────────────────────────────────────────────────────────────────
export interface FeedbackCommentEditorProps
  extends Omit<
    React.ComponentProps<typeof EditorProvider>,
    'extensions' | 'editor'
  > {
  editorKey: string | undefined // memoises drafts
  onSubmit?: (state: string, id?: string) => Promise<void>
  onCancel?: () => void
  setEditor?: (editor: Editor) => void
  className?: string
  alwaysShowPlaceholder?: boolean
  placeholder?: string
  mediaSize?: { width: number; height: number }
  initialHeight?: number
}

// ────────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────────
const EDITABLE_MIN_HEIGHT = 42

const normalizeUser = (user: PublicUser) => {
  let str = `${user.firstName} ${user.lastName}`.trim()
  if (!str) str = user.username ?? ''
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[^a-z0-9]/g, '')
}

const searchUsers = (
  query: string,
  items: MentionsListProps<PublicUser>['items']
) => {
  const normalizedQuery = query
    .toLowerCase()
    .normalize('NFD')
    .replace(/[^a-z0-9]/g, '')

  return items
    .filter(
      (item) =>
        normalizeUser(item).includes(normalizedQuery) ||
        item.username?.includes(normalizedQuery)
    )
    .sort((a, b) => {
      const aIndex = normalizeUser(a).indexOf(normalizedQuery)
      const bIndex = normalizeUser(b).indexOf(normalizedQuery)
      return aIndex === bIndex
        ? normalizeUser(a).localeCompare(normalizeUser(b))
        : aIndex - bIndex
    })
    .slice(0, 5)
}

// ────────────────────────────────────────────────────────────────────────────────
// Main Component
// ────────────────────────────────────────────────────────────────────────────────
export const FeedbackCommentEditor: React.FC<FeedbackCommentEditorProps> = ({
  editorKey,
  className,
  alwaysShowPlaceholder,
  onCancel,
  placeholder = 'Reply',
  mediaSize,
  initialHeight,
  setEditor,
  onCreate,
  ...rest
}) => {
  // ░░░ Stores ░░░
  const { data: repoMembers } = useRepoMembers()
  const currentUser = useUserStore((s) => s.optimisticUser)
  const repoMembersRef = React.useRef<PublicUser[]>([])
  repoMembersRef.current =
    repoMembers
      ?.map((u) => ({ ...u, username: u.username?.trim() ?? '' }))
      .filter((u) => u.username !== currentUser?.username) || []

  const treeRef = useWorkingTreeStore((s) => s.treeRef)
  const lastTabOpenedHistoryMap = useTabsStore((s) => s.lastOpenedHistoryMap)
  const { data: replicacheData } = useRepoReplicache()
  const teamId = replicacheData?.team.id
  const repoId = replicacheData?.repo.id

  // ░░░ Draft ░░░
  const { draft, setDraft } = useCommentDraftStorage(editorKey)

  // ░░░ Editor instance ░░░
  const [editor, setInternalEditor] = React.useState<Editor | null>(null)

  // ──────────────────────────────────────────────────────────────────────────────
  // Editor Props (paste / drop handlers)
  // ──────────────────────────────────────────────────────────────────────────────
  const baseEditorProps: EditorProviderProps['editorProps'] =
    React.useMemo(() => {
      if (!teamId || !repoId) return {}

      return {
        handlePaste(view, event, slice) {
          if (!teamId || !repoId) return false

          // File paste
          const file = event.clipboardData?.files?.[0]
          if (file) {
            event.preventDefault()
            const pos = view.state.selection.from

            if (file.type.includes('image/')) {
              startImageUpload(file, view, pos, { teamId, repoId })
            } else if (file.type.includes('video/')) {
              startVideoUpload(file, view, pos, { teamId, repoId })
            } else if (SUPPORTED_LLM_FILE_TYPES.includes(file.type)) {
              startFileUpload(file, view, pos, { teamId, repoId })
            } else {
              toast.error('Unsupported file type')
            }
            return true
          }

          // HTML / Nodes paste containing images from outside
          if (slice.content.childCount > 0) {
            const images: string[] = []
            slice.content.descendants((node) => {
              if (
                node.type.name === 'image' &&
                !node.attrs.src.startsWith(productionR2Origin)
              ) {
                images.push(node.attrs.src)
              }
            })
            if (images.length) {
              setTimeout(
                () => uploadAndReplaceImages(images, view, { teamId, repoId }),
                100
              )
            }
          }
          return false
        },

        handleDrop(view, event, slice, moved) {
          if (!teamId || !repoId || moved) return false

          const file = event.dataTransfer?.files?.[0]
          if (file) {
            event.preventDefault()
            const coords = view.posAtCoords({
              left: event.clientX,
              top: event.clientY,
            })
            if (!coords) return false
            const pos = coords.pos - 1 // adjust so we don't create extra node

            if (file.type.includes('image/')) {
              getImageDimensions(file).then(({ width, height }) =>
                startImageUpload(
                  file,
                  view,
                  pos,
                  { teamId, repoId },
                  width && height ? { width, height } : undefined
                )
              )
            } else if (SUPPORTED_LLM_FILE_TYPES.includes(file.type)) {
              startFileUpload(file, view, pos, { teamId, repoId })
            } else {
              toast.error('Unsupported file type')
              extractVideoThumbnailAndDimensions(file).then(
                ({ width, height, thumbnail }) =>
                  startVideoUpload(
                    file,
                    view,
                    pos,
                    { teamId, repoId },
                    {
                      ...(width && height && { width, height }),
                      ...(thumbnail && { thumbnail }),
                    }
                  )
              )
            }
            return true
          }

          // Dropping nodes containing outside images
          if (slice?.content.childCount) {
            const imgs: string[] = []
            slice.content.descendants((node) => {
              if (node.type.name === 'image') imgs.push(node.attrs.src)
            })
            const externalImgs = imgs.filter(
              (url) => !url.startsWith(productionR2Origin)
            )
            if (externalImgs.length) {
              setTimeout(
                () =>
                  uploadAndReplaceImages(externalImgs, view, {
                    teamId,
                    repoId,
                  }),
                100
              )
            }
          }
          return false
        },
      }
    }, [teamId, repoId])

  // Merge props from caller
  const mergedEditorProps = React.useMemo(
    () => ({ ...baseEditorProps, ...rest.editorProps }),
    [baseEditorProps, rest.editorProps]
  )

  // ──────────────────────────────────────────────────────────────────────────────
  // Mention / Block search helpers
  // ──────────────────────────────────────────────────────────────────────────────

  const { search: performSearch, results: _searchResults } = useSearchRepo()
  const [blockSearchResults, setBlockSearchResults] = React.useState<
    Array<{
      id: string
      title: string
      type: string
    }>
  >([])

  const searchBlocks = React.useCallback(
    async (searchTerm: string | null) => {
      if (!searchTerm?.trim()) {
        // Show recent blocks when no search term
        const allBlockIds = Array.from(lastTabOpenedHistoryMap.keys())
        const recentBlocks = allBlockIds
          .filter((id) => treeRef.current?.getNode(id)) // Only include blocks that exist in current tree
          .sort((a, b) => {
            const aLastOpened = lastTabOpenedHistoryMap.get(a) ?? 0
            const bLastOpened = lastTabOpenedHistoryMap.get(b) ?? 0
            return bLastOpened - aLastOpened
          })
          .slice(0, 5)
          .map((id) => {
            const node = treeRef.current?.getNode(id)
            return {
              id,
              title:
                (node?.value as any)?.title ||
                (node?.value as any)?.name ||
                'Untitled',
              type: node?.value?.type || 'document',
            }
          })

        setBlockSearchResults(recentBlocks)
        return
      }

      try {
        const results = await performSearch({
          q: searchTerm,
          by: ['content', 'title'],
          limit: 10,
        })

        // Transform search results to match expected format
        const transformedResults = results.map((result) => ({
          id: result.blockId,
          title:
            result.highlight?.find((h) => h.by === 'title')?.snippet ||
            'Untitled',
          type: 'document', // Default type
        }))

        setBlockSearchResults(transformedResults)
      } catch (error) {
        console.error('Block search error:', error)
        setBlockSearchResults([])
      }
    },
    [performSearch, lastTabOpenedHistoryMap, treeRef]
  )

  const results = React.useCallback(
    (search: string | null) => {
      // Trigger async search
      searchBlocks(search)
      // Return current results
      return blockSearchResults
    },
    [searchBlocks, blockSearchResults]
  )

  // ──────────────────────────────────────────────────────────────────────────────
  // Extensions
  // ──────────────────────────────────────────────────────────────────────────────
  const extensions = React.useMemo(() => {
    return getFeedbackExtensions({
      mediaSize,
      placeholder,

      getNode: (id: string): TreeNode<Block> | null =>
        treeRef.current?.getNode(id) ?? null,

      handleEnter: ({ editor }) => {
        const submitBtn = editor.view.dom
          .closest('.comment-tiptap-editor')
          ?.querySelector<HTMLButtonElement>('[data-comment-submit-button]')
        if (!submitBtn) throw new Error('Submit button not found')

        submitBtn.click()
      },

      ...(onCancel && {
        handleEscape: ({ editor }) => {
          onCancel?.()
          editor.commands.clearContent()
        },
      }),

      getUsers: (q) =>
        !q ? repoMembersRef.current : searchUsers(q, repoMembersRef.current),

      getBlocks: (q): BlockMentionProps[] => {
        const items = results(q)
        return items
          .map((item) => {
            const node = treeRef.current?.getNode(item.id)
            invariant(node, 'Node not found')

            const parent = node?.parentKey
              ? treeRef.current?.getNode(node.parentKey)
              : null
            const grand = parent?.parentKey
              ? treeRef.current?.getNode(parent.parentKey)
              : null
            const paths = [grand, parent]
              .filter(
                (n): n is TreeNode<Block> => !!n && n.value.type !== 'root'
              )
              .map((n) => n.value)

            return { block: node.value, paths } as BlockMentionProps
          })
          .filter(Boolean)
      },
    })
  }, [mediaSize, placeholder, onCancel, treeRef, results])

  // ──────────────────────────────────────────────────────────────────────────────
  // Sync Draft <-> Editor
  // ──────────────────────────────────────────────────────────────────────────────
  React.useEffect(() => {
    if (!editor) return

    // Load draft once
    if (draft.value && editor.isEmpty) {
      editor.commands.setContent(JSON.parse(draft.value) as JSONContent)
      //generate a dummy transaction to trigger the update in a timeout
      setTimeout(() => {
        editor.view.dispatch(editor.view.state.tr)
      }, 100)
    }

    const updateDraft = debounce(() => {
      const value = JSON.stringify(editor.getJSON())
      setDraft((prev) => {
        if (prev?.value === value) return prev // no change
        return prev ? { ...prev, value } : { id: getId(), value }
      })
    }, 500)

    editor.on('update', updateDraft)
    return () => {
      editor.off('update', updateDraft)
      updateDraft.flush()
    }
  }, [editor, draft.value, draft.id, setDraft])

  // ──────────────────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────────────────
  return (
    <PrimitiveFeedbackCommentEditor
      key={editorKey /* stable key: don't append draft.id */}
      className={clsx(className, 'rt-editor')}
      style={{
        ['--editable-min-height' as string]: EDITABLE_MIN_HEIGHT,
        minHeight:
          initialHeight && initialHeight !== EDITABLE_MIN_HEIGHT
            ? initialHeight
            : 'auto',
      }}
      extensions={extensions}
      editorProps={mergedEditorProps}
      alwaysShowPlaceholder={alwaysShowPlaceholder}
      immediatelyRender={false}
      content={
        draft.value ? (JSON.parse(draft.value) as JSONContent) : undefined
      }
      onCreate={({ editor }) => {
        setInternalEditor(editor)
        setEditor?.(editor)
        onCreate?.({ editor })
      }}
      {...rest}
    />
  )
}

// ────────────────────────────────────────────────────────────────────────────────
// Primitive Wrapper (unchanged visual)
// ────────────────────────────────────────────────────────────────────────────────
interface PrimitiveProps extends EditorProviderProps {
  alwaysShowPlaceholder?: boolean
  className?: string
  style?: React.CSSProperties
}

export const PrimitiveFeedbackCommentEditor: React.FC<PrimitiveProps> =
  React.memo(({ style, alwaysShowPlaceholder, className, ...props }) => {
    return (
      <div
        className={clsx(
          'primitive-tiptap-editor',
          'comment-tiptap-editor',
          'rt-editor',
          vcursorstyles['fake-cursor'],
          mentionClasses.mentions,
          'max-w-none flex-1 flex flex-col justify-between break-before-all [word-break:break-word] gap-4 [&_.ProseMirror]:!py-0',
          alwaysShowPlaceholder && 'always-show-placeholder',
          className
        )}
        style={style}
      >
        <EditorProvider {...props}>
          <ContentSynchronizer content={props.content} />
        </EditorProvider>
      </div>
    )
  })

// Keeps view-only editors in sync when `content` prop changes
const ContentSynchronizer: React.FC<{ content: Content | undefined }> = ({
  content,
}) => {
  const { editor } = useCurrentEditor()
  const initialSyncDone = React.useRef(false)

  React.useEffect(() => {
    if (!editor || editor.isEditable) return
    if (content && initialSyncDone.current) editor.commands.setContent(content)
    initialSyncDone.current = true
  }, [content, editor])

  return null
}
